/**
 * Track pictures.
 *
 * The images live in the client's own public folder (`public/img/tracks/`), and the file name is the one the
 * track file writes down (`backgroundImage`): the track carries its picture's name and format with it, so
 * nothing here has to guess an extension from the id.
 */
export function trackImage(file)
{
  return `/img/tracks/${file}`
}

/**
 * The shrunk copy of a picture, as a page shows it (see scripts/build-track-previews.mjs): same name, always
 * WebP. The original is painted at one pixel per world unit — a file of several megabytes — and that size
 * only pays off where the pixels are stretched over the world (the replay, the editor).
 */
export function trackPreviewImage(file)
{
  return `/img/tracks/preview/${file.replace(/\.[^./]+$/, '')}.webp`
}

/**
 * The name the site's copy of a track file gives its picture (`public/tracks/<id>.json`): null when there is
 * no such file (a track the editor is still building) or it names no picture.
 */
export async function trackPictureFile(trackId)
{
  try {
    const response = await fetch(`/tracks/${trackId}.json`)
    if (!response.ok) return null
    return (await response.json()).backgroundImage || null
  } catch {
    return null
  }
}

/** The same name, already turned into the address of the picture in the site. */
export async function trackPicture(trackId)
{
  const file = await trackPictureFile(trackId)
  return file ? trackImage(file) : null
}

/**
 * The working background the track editor paints behind the drawing: a picture named after the track's
 * id in `public/img/tracks/editor_backgrounds/`, kept apart from the track's own finished picture (the
 * one the game shows). It is a working aid, not an asset the track carries, so nothing names its format
 * and every candidate is tried until one loads — null when the track has none.
 */
const EDITOR_BACKGROUND_FORMATS = ['webp', 'jpg', 'jpeg', 'png']

export function trackEditorBackground(trackId)
{
  const base = `/img/tracks/editor_backgrounds/${trackId}`
  return new Promise((resolve) => {
    let index = 0
    const tryNext = () => {
      if (index >= EDITOR_BACKGROUND_FORMATS.length) { resolve(null); return }
      const url = `${base}.${EDITOR_BACKGROUND_FORMATS[index++]}`
      const img = new Image()
      img.onload = () => resolve(url)
      img.onerror = tryNext
      img.src = url
    }
    tryNext()
  })
}
